import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RouteConstants } from "../../constants/route-constant";
import SmartFhirLoadingScreen from "../Shared/SmartFhirLoadingScreen";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setProvider } from "../../store/providerReducer";
import { useFhirInitialization } from "../../hooks/useFhirInitialization";
import { useFhirClient } from "../../hooks/useFhirClient";
import { useHomeModuleAlertCounts } from "../../hooks/useHomeModuleAlertCounts";
import { STORAGE_KEYS } from "../../services/encrypt-decrypt";
import {
  prefetchCdiReferenceData,
  prefetchHomeReferenceData,
  prefetchClinicalCareGuidelines,
  prefetchEncoderCatalog,
  invalidateCareManagementQuery,
} from "../../hooks/queries";
import { DEFAULT_ENCODER_CATALOG } from "../../utils/encoder";
import { useProviderDetails } from "../../hooks/queries";
import {
  getHomeScreenProviderLabel,
} from "../../utils/provider-display-utils";
import SimpleCircularIndicator from "../Shared/indicator";
import useColors from "../../theme/useColors";

interface ModuleCardProps {
  title: string;
  description: string;
  logoSrc?: string;
  comingSoon?: boolean;
  alertCount?: number;
  onClick?: () => void;
  onPrefetch?: () => void;
}

const ACTIVE_CARD_CLASSES =
  "bg-white rounded-2xl border border-[#BDBDBD]/40 shadow relative overflow-hidden z-[1] w-full h-[200px] flex flex-col hover:shadow-lg transition-shadow cursor-pointer";

const COMING_SOON_CARD_CLASSES =
  "bg-white rounded-2xl border border-[#BDBDBD]/40 shadow relative overflow-hidden z-[1] w-full h-[200px] flex flex-col cursor-default opacity-80 transition-shadow";

const ModuleCard: React.FC<ModuleCardProps> = ({
  title,
  description,
  logoSrc,
  comingSoon = false,
  alertCount = 0,
  onClick,
  onPrefetch,
}) => {
  const colors = useColors();
  const outerClasses = comingSoon ? COMING_SOON_CARD_CLASSES : ACTIVE_CARD_CLASSES;

  const card = (
    <div
      className={outerClasses}
      onClick={comingSoon ? undefined : onClick}
      onMouseEnter={comingSoon ? undefined : onPrefetch}
      onFocus={comingSoon ? undefined : onPrefetch}
      role={comingSoon ? undefined : "button"}
      tabIndex={comingSoon ? undefined : 0}
      onKeyDown={
        comingSoon
          ? undefined
          : (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick?.();
              }
            }
      }
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-[150px] -right-[300px] -z-10 blur-3xl h-[300px] w-[400px] rounded-full bg-gradient-to-t from-[#FDECD4] to-[#FAF8F1]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-[150px] -left-[300px] -z-10 blur-3xl h-[300px] w-[400px] rounded-full bg-gradient-to-t from-[#FAF8F1] to-[#E7D0F3]"
      />
      <img
        src="/favicon.svg"
        alt=""
        aria-hidden
        className="pointer-events-none absolute bottom-6 right-6 z-[1] h-4 w-auto shrink-0 object-contain opacity-70"
      />
      {comingSoon && (
        <span className="absolute top-3 right-3 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500">
          Coming Soon
        </span>
      )}
      <div className="flex flex-1 flex-wrap gap-1 p-3 text-sm font-medium text-[#847984]">
        <div className="flex h-full flex-col justify-between gap-1 p-3">
          <div className="flex items-start gap-2">
            <img
              src="/ai-bulwark-logo.svg"
              alt=""
              aria-hidden
              className="pointer-events-none mt-[5px] h-5 w-auto shrink-0 object-contain opacity-95 sm:h-6"
            />
            <span className="header-gradient whitespace-pre-line text-2xl font-bold leading-tight">
              {title}
            </span>
          </div>
          <div className="mt-auto flex flex-col gap-2">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt=""
                aria-hidden
                className="pointer-events-none h-4 w-auto max-w-[70%] shrink-0 object-contain object-left"
              />
            ) : null}
            <p className="line-clamp-3 whitespace-pre-line text-sm">
              {description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (!comingSoon && alertCount > 0) {
    return (
      <SimpleCircularIndicator
        count={alertCount}
        color={colors.danger[4]}
        size={28}
        fullWidth
      >
        {card}
      </SimpleCircularIndicator>
    );
  }

  return card;
};

const ModuleNavigation: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fhirState = useFhirInitialization();
  const fhirClient = useFhirClient();
  useHomeModuleAlertCounts();
  const providerNPI = useAppSelector((state) => state?.provider?.providerNPI);
  const provider = useAppSelector((state) => state?.provider?.provider);
  const messageCount = useAppSelector((state) => state?.messages?.count);
  const {
    vbcCount,
    cdiCount,
    myQueriesCount,
    umQueriesCount,
    opcdQueriesCount,
    opcdHimAlertsCount,
    opcdCodingAlertsCount,
  } = useAppSelector((state) => state?.provider);

  const providerInfo = provider?.provider_info;
  const practitioner = fhirState?.practitioner;

  const providerLabel = getHomeScreenProviderLabel(
    providerInfo,
    fhirClient,
    practitioner
  );

  const { data: providerDetailsResponse } = useProviderDetails(providerNPI);

  useEffect(() => {
    if (providerDetailsResponse?.data?.data) {
      dispatch(setProvider(providerDetailsResponse.data.data));
    }
  }, [dispatch, providerDetailsResponse]);

  useEffect(() => {
    if (!fhirState.isInitialized) {
      return;
    }

    const token = localStorage.getItem(STORAGE_KEYS.JWT_TOKEN);
    const hasToken =
      token && token !== "" && token !== "null" && token !== "undefined";

    if (hasToken) {
      prefetchHomeReferenceData(providerNPI || undefined);
    }
  }, [fhirState.isInitialized, providerNPI]);

  useEffect(() => {
    if (fhirState?.hasFailed) {
      navigate(RouteConstants.ROUTE_UNAUTHORIZED);
    }
  }, [fhirState?.hasFailed, navigate]);

  const cdiAlertCount =
    Number(messageCount || 0) +
    Number(cdiCount || 0) +
    Number(myQueriesCount || 0);
  // My Queries + Messages + UM Alerts (UM Alerts temporarily uses CDI alerts pending count)
  const umAlertCount =
    Number(umQueriesCount || 0) +
    Number(messageCount || 0) +
    Number(cdiCount || 0);
  const vbcAlertCount = Number(vbcCount || 0);
  const opcdAlertCount =
    Number(opcdQueriesCount || 0) +
    Number(opcdHimAlertsCount || 0) +
    Number(opcdCodingAlertsCount || 0);

  const modules: Array<ModuleCardProps & { title: string; description: string }> = [
    {
      title: "Inpatient CDI",
      description: "Provider queries for Inpatient Clinical Documentation Integrity (IP-CDI)",
      logoSrc: "/arcplus-primary.svg",
      comingSoon: false,
      alertCount: cdiAlertCount,
      onClick: () => navigate(RouteConstants.ROUTE_CDI_MODULE),
      onPrefetch: prefetchCdiReferenceData,
    },
    {
      title: "Utilization Management",
      description:
        "AI for UM, Readmission Intelligence & Clinical Level-of-Care Evaluation",
      logoSrc: "/auricle-logo.webp",
      comingSoon: false,
      alertCount: umAlertCount,
      onClick: () => navigate(RouteConstants.ROUTE_UM_MODULE),
    },
    {
      title: "CAiRE",
      description:
        "Clinical AI-enabled Readmission Evaluator for PAC management",
      logoSrc: "/care-logo.png",
      comingSoon: false,
      alertCount: 0,
      onClick: () => {
        void invalidateCareManagementQuery();
        navigate(RouteConstants.ROUTE_CAIRE_MODULE);
      },
    },
    {
      title: "Value Based Care",
      description: "Risk Adjustment, Quality & Risk Stratification for VBC",
      logoSrc: "/RAQPlusPrimary.svg",
      comingSoon: false,
      alertCount: vbcAlertCount,
      onClick: () => navigate(RouteConstants.ROUTE_VBC_MODULE),
    },
    {
      title: "Outpatient CDI",
      description:
        "Provider Queries for Outpatient Clinical Documentation Integrity (OP-CDI)",
      logoSrc: "/arcplus-primary.svg",
      comingSoon: false,
      alertCount: opcdAlertCount,
      onClick: () => navigate(RouteConstants.ROUTE_OPCD_MODULE),
    },
    {
      title: "Clinical Care Guidelines",
      description:
        "Bulwark AI Clinical Care Guidelines for Medical Necessity Reviews",
      comingSoon: false,
      alertCount: 0,
      onClick: () => navigate(RouteConstants.ROUTE_CLINICAL_CARE_GUIDELINES),
      onPrefetch: prefetchClinicalCareGuidelines,
    },
    {
      title: "Encoder",
      description: DEFAULT_ENCODER_CATALOG.description,
      comingSoon: false,
      alertCount: 0,
      onClick: () => navigate(RouteConstants.ROUTE_ENCODER),
      onPrefetch: prefetchEncoderCatalog,
    },
  ];

  if (fhirState.isInitializing || !fhirState.isInitialized) {
    return (
      <div className="h-screen w-full">
        <SmartFhirLoadingScreen />
      </div>
    );
  }

  return (
    <div className="relative flex h-screen w-full min-h-0 flex-col overflow-hidden">
      <div className="w-full shrink-0 bg-white px-6 pb-4 pt-3 md:px-8">
        <div className="mx-auto flex w-full max-w-[1600px] items-center justify-between gap-4">
          <img
            src="/org-logo.svg"
            alt=""
            className="block h-auto min-w-0 max-h-14 w-auto object-contain object-left sm:max-h-16 md:max-h-20 lg:max-h-[6rem]"
          />
          <p className="shrink-0 text-sm font-medium text-gray-900">
            {providerLabel}
          </p>
        </div>
      </div>

      <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
        <div className="w-full px-6 py-6 md:px-8">
          <div className="mx-auto grid w-full max-w-[1600px] grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {modules.map((module) => (
              <ModuleCard
                key={module.title}
                title={module.title}
                description={module.description}
                logoSrc={module.logoSrc}
                comingSoon={module.comingSoon}
                alertCount={module.alertCount}
                onClick={module.onClick}
                onPrefetch={module.onPrefetch}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleNavigation;
